import {
  ClipboardCheck, 
  Calendar, 
  Package, 
  Truck, 
  Factory, 
  Clock, 
  AlertTriangle,
  XCircle,
  Check,
  Edit2,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { ExecutionPlan } from '@/types/agent-events';
import { fmtVolume } from '@/lib/utils';

interface ExecutionPlanCardProps {
  plan: ExecutionPlan;
  onConfirm: () => void;
  onModify: () => void;
  onCancel: () => void;
  isConfirming?: boolean;
}


const getIntentConfig = (intent: ExecutionPlan['intent']) => {
  switch (intent) {
    case 'create_order':
      return {
        label: 'Plan de commande',
        icon: Package,
        color: 'bg-primary',
        borderColor: 'border-primary/30',
        bgColor: 'bg-primary/5',
      };
    case 'reserve_trucks':
      return {
        label: 'Plan de réservation',
        icon: Truck,
        color: 'bg-info',
        borderColor: 'border-info/30',
        bgColor: 'bg-info/5',
      };
    case 'cancel_order':
    case 'cancel_reservation':
      return {
        label: 'Plan d\'annulation',
        icon: XCircle,
        color: 'bg-destructive',
        borderColor: 'border-destructive/30',
        bgColor: 'bg-destructive/5',
      };
    case 'update_progress':
      return {
        label: 'Mise à jour avancement',
        icon: ClipboardCheck,
        color: 'bg-success',
        borderColor: 'border-success/30',
        bgColor: 'bg-success/5',
      };
    default:
      return {
        label: 'Plan d\'exécution',
        icon: ClipboardCheck,
        color: 'bg-primary',
        borderColor: 'border-primary/30',
        bgColor: 'bg-primary/5',
      };
  }
};

function formatDateFr(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'EEEE d MMMM', { locale: fr });
  } catch {
    return dateStr;
  }
}

export function ExecutionPlanCard({ 
  plan, 
  onConfirm, 
  onModify, 
  onCancel,
  isConfirming = false 
}: ExecutionPlanCardProps) {
  const config = getIntentConfig(plan.intent);
  const IconComponent = config.icon;
  const hasMissingFields = plan.missing_fields.length > 0;
  const hasRisks = plan.risks.length > 0;

  return (
    <div className="mb-lg">
      <Card className={`border ${config.borderColor} ${config.bgColor}`}>
        <CardHeader className="px-lg">
          <div className="flex items-center justify-between gap-sm">
            <div className="flex items-center gap-sm.5">
              <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
                <IconComponent className="w-4 h-4 text-primary-foreground" />
              </div>
              <CardTitle className="text-card-title">
                {config.label}
              </CardTitle>
            </div>
            <Badge 
              variant={hasMissingFields ? "destructive" : "secondary"} 
              className="text-label-sm"
            >
              {hasMissingFields ? 'Incomplet' : 'Prêt'}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="px-lg space-y-md">
          {/* Date */}
          {plan.inputs_normalized.date && (
            <div className="flex items-center gap-sm text-body-md text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{formatDateFr(plan.inputs_normalized.date)}</span>
            </div>
          )}

          {/* Ouvrages for create_order */}
          {plan.inputs_normalized.ouvrages && plan.inputs_normalized.ouvrages.length > 0 && (
            <div className="space-y-1.5">
              {plan.inputs_normalized.ouvrages.map((ouvrage, i) => (
                <div 
                  key={i} 
                  className="flex items-center justify-between p-sm rounded-lg bg-background/80 border border-border/50 text-body-md"
                >
                  <span className="font-medium">{ouvrage.name}</span>
                  <div className="flex items-center gap-sm text-muted-foreground">
                    <span>{fmtVolume(ouvrage.volume)} m³</span>
                    {ouvrage.formula && ouvrage.formula !== 'Non spécifiée' && (
                      <Badge variant="outline" className="text-label-sm">{ouvrage.formula}</Badge>
                    )}
                    {ouvrage.heure && ouvrage.heure !== 'Non spécifiée' && (
                      <span className="flex items-center gap-xs">
                        <Clock className="w-3 h-3" />
                        {ouvrage.heure}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Truck reservation details */}
          {plan.intent === 'reserve_trucks' && (
            <div className="space-y-1.5 text-body-md">
              <div className="flex items-center gap-sm text-muted-foreground">
                <Truck className="w-4 h-4" />
                <span>{plan.inputs_normalized.truck_count} camion(s)</span>
              </div>
              {plan.inputs_normalized.first_truck_time && (
                <div className="flex items-center gap-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>Premier camion à {plan.inputs_normalized.first_truck_time}</span>
                </div>
              )}
            </div>
          )}

          {/* Volume total */}
          {plan.inputs_normalized.total_volume && (
            <div className="flex items-center gap-sm text-body-md text-muted-foreground">
              <Package className="w-4 h-4" />
              <span>
                Volume total: <span className="font-semibold text-foreground">{fmtVolume(plan.inputs_normalized.total_volume)} m³</span>
              </span>
              {plan.computed_values.truck_count && (
                <span className="text-helper">
                  ({plan.computed_values.truck_count} camion{plan.computed_values.truck_count > 1 ? 's' : ''})
                </span>
              )}
            </div>
          )}

          {/* Plant */}
          {plan.inputs_normalized.plant && (
            <div className="flex items-center gap-sm text-body-md text-muted-foreground">
              <Factory className="w-4 h-4" />
              <span>Centrale: {plan.inputs_normalized.plant}</span>
            </div>
          )}

          {/* Options */}
          {plan.inputs_normalized.options && plan.inputs_normalized.options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {plan.inputs_normalized.options.map((option, i) => (
                <Badge key={i} variant="outline" className="text-label-sm">
                  {option}
                </Badge>
              ))}
            </div>
          )}

          {/* Risks */}
          {hasRisks && (
            <div className="p-2.5 rounded-lg bg-warning/10 border border-warning/30">
              <div className="flex items-start gap-sm">
                <AlertTriangle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                <div className="space-y-xs">
                  {plan.risks.map((risk, i) => (
                    <p key={i} className="text-helper text-warning">{risk}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Missing fields */}
          {hasMissingFields && (
            <div className="p-2.5 rounded-lg bg-destructive/10 border border-destructive/30">
              <div className="flex items-start gap-sm">
                <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-body-sm text-destructive mb-1.5">
                    Informations manquantes:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {plan.missing_fields.map((field, i) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="text-label-sm border-destructive/50 text-destructive"
                      >
                        {field}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="px-lg gap-sm">
          <Button
            onClick={onConfirm}
            size="sm"
            className="flex-1 h-9 gap-1.5"
            disabled={hasMissingFields || isConfirming}
          >
            {isConfirming ? (
              <>
                <span className="animate-spin">⏳</span>
                Confirmation...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Confirmer
              </>
            )}
          </Button>
          
          <Button
            onClick={onModify}
            variant="outline"
            size="sm"
            className="h-9 gap-1.5"
            disabled={isConfirming}
          >
            <Edit2 className="w-4 h-4" />
            Modifier
          </Button>
          
          <Button
            onClick={onCancel}
            variant="ghost"
            size="sm"
            className="h-9 text-destructive hover:text-destructive hover:bg-destructive/10"
            disabled={isConfirming}
          >
            <X className="w-4 h-4" />
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
